// Australian resident ANNUAL tax rate tables, keyed by FY (the year it ENDS
// in — see fy.js). Source: ATO "Individual income tax rates", "Medicare levy
// surcharge income, thresholds and rates" and "Study and training loan
// repayment thresholds and rates" (copies in the finance plan's scratch dir).
//
// Amounts here are WHOLE DOLLARS (as published) — the calculators in tax.js
// convert to/from integer cents. Rates are decimals.
//
// Known gaps (documented, deliberately not guessed):
//  - Low income tax offset (LITO/LMITO) and the Medicare levy low-income
//    reduction are NOT modelled — both only matter well below typical
//    professional incomes. `annualTax` reports them as unmodelled.
//  - MLS thresholds before 2022–23 aren't in our source; earlier years fall
//    back to the oldest table we have (`MLS_FALLBACK_NOTE`).
//  - HELP/STSL compulsory repayment tables start at 2020–21.

/**
 * Resident income-tax brackets: rows of [threshold, baseTax, marginalRate]
 * meaning "income over `threshold`: baseTax + rate × (income − threshold)".
 * Bases are the ATO's published figures (not re-derived).
 */
const BRACKETS_2013_2016 = [
  [18_200, 0, 0.19],
  [37_000, 3_572, 0.325],
  [80_000, 17_547, 0.37],
  [180_000, 54_547, 0.45],
];
const BRACKETS_2017_2018 = [
  [18_200, 0, 0.19],
  [37_000, 3_572, 0.325],
  [87_000, 19_822, 0.37],
  [180_000, 54_232, 0.45],
];
const BRACKETS_2019_2020 = [
  [18_200, 0, 0.19],
  [37_000, 3_572, 0.325],
  [90_000, 20_797, 0.37],
  [180_000, 54_097, 0.45],
];
const BRACKETS_2021_2024 = [
  [18_200, 0, 0.19],
  [45_000, 5_092, 0.325],
  [120_000, 29_467, 0.37],
  [180_000, 51_667, 0.45],
];
const BRACKETS_2025_2026 = [
  [18_200, 0, 0.16],
  [45_000, 4_288, 0.3],
  [135_000, 31_288, 0.37],
  [190_000, 51_638, 0.45],
];
const BRACKETS_2027 = [
  [18_200, 0, 0.15],
  [45_000, 4_020, 0.3],
  [135_000, 31_020, 0.37],
  [190_000, 51_370, 0.45],
];
const BRACKETS_2028_ON = [
  [18_200, 0, 0.14],
  [45_000, 3_752, 0.3],
  [135_000, 30_752, 0.37],
  [190_000, 51_102, 0.45],
];

export const RESIDENT_BRACKETS = {
  2013: BRACKETS_2013_2016,
  2014: BRACKETS_2013_2016,
  2015: BRACKETS_2013_2016,
  2016: BRACKETS_2013_2016,
  2017: BRACKETS_2017_2018,
  2018: BRACKETS_2017_2018,
  2019: BRACKETS_2019_2020,
  2020: BRACKETS_2019_2020,
  2021: BRACKETS_2021_2024,
  2022: BRACKETS_2021_2024,
  2023: BRACKETS_2021_2024,
  2024: BRACKETS_2021_2024,
  2025: BRACKETS_2025_2026,
  2026: BRACKETS_2025_2026,
  2027: BRACKETS_2027,
  // Legislated (Treasury Laws Amendment (More Cost of Living Relief) Act 2025):
  // the bottom rate falls to 14% from 2027–28. Later years reuse the latest
  // legislated brackets (see `residentBrackets`).
  2028: BRACKETS_2028_ON,
};

/** Brackets for an FY; years past the table use the latest legislated rates. */
export function residentBrackets(fy) {
  const years = Object.keys(RESIDENT_BRACKETS).map(Number);
  const last = Math.max(...years);
  return RESIDENT_BRACKETS[fy > last ? last : fy] ?? null;
}

/**
 * The latest FY whose indexed thresholds (MLS, study loan) are published;
 * later years reuse that year's figures (`annualTax` adds a note).
 */
export const LATEST_THRESHOLD_FY = 2027;

/** Medicare levy rate: 1.5% to 2013–14, 2% from 2014–15. */
export function medicareLevyRate(fy) {
  return fy <= 2014 ? 0.015 : 0.02;
}

/** Temporary Budget Repair Levy: 2% of taxable income over $180k, 2014–15 to 2016–17. */
export function budgetRepairLevy(fy) {
  return fy >= 2015 && fy <= 2017 ? { threshold: 180_000, rate: 0.02 } : null;
}

/**
 * Medicare levy surcharge tiers. `single`/`family` are the UPPER bounds of the
 * base tier, tier 1 and tier 2 (tier 3 is anything above); income at or below
 * a bound stays in that tier. Family bounds rise by `childStep` for each
 * dependent child after the first.
 */
const MLS_RATES = [0, 0.01, 0.0125, 0.015];
export const MLS_TABLES = {
  2023: { single: [90_000, 105_000, 140_000], family: [180_000, 210_000, 280_000] },
  2024: { single: [93_000, 108_000, 144_000], family: [186_000, 216_000, 288_000] },
  2025: { single: [97_000, 113_000, 151_000], family: [194_000, 226_000, 302_000] },
  2026: { single: [101_000, 118_000, 158_000], family: [202_000, 236_000, 316_000] },
  2027: { single: [105_000, 123_000, 164_000], family: [210_000, 246_000, 328_000] },
};
export const MLS_CHILD_STEP = 1_500;
/**
 * With a spouse, no MLS is payable if your OWN MLS income is at or below the
 * individual Medicare low-income threshold. Our source only gives the 2025–26
 * figure, so it's used for every year (a small, documented approximation).
 */
export const MLS_SPOUSE_LOW_INCOME = 27_222;
export const MLS_FALLBACK_NOTE =
  'Medicare levy surcharge thresholds before 2022–23 are not tabulated; the 2022–23 table was used.';

/** MLS table for an FY (latest-known for future years, oldest-known for earlier ones). */
export function mlsTable(fy) {
  const years = Object.keys(MLS_TABLES).map(Number);
  const first = Math.min(...years);
  const last = Math.max(...years);
  const key = Math.min(Math.max(fy, first), last);
  return { ...MLS_TABLES[key], rates: MLS_RATES, fallback: fy < first };
}

/**
 * Compulsory HELP/STSL repayment. Up to 2024–25 a single rate applies to the
 * WHOLE repayment income (banded: `bands` = lower bounds for `BANDED_RATES`);
 * from 2025–26 it's marginal (15% / base + 17% / 10% of total).
 */
const BANDED_RATES = [
  0.01, 0.02, 0.025, 0.03, 0.035, 0.04, 0.045, 0.05, 0.055, 0.06, 0.065, 0.07, 0.075, 0.08, 0.085,
  0.09, 0.095, 0.1,
];
export const HELP_REPAYMENT = {
  2021: {
    rates: BANDED_RATES,
    bands: [
      46_620, 53_827, 57_056, 60_480, 64_109, 67_955, 72_032, 76_355, 80_936, 85_793, 90_940,
      96_397, 102_180, 108_310, 114_810, 121_699, 129_000, 136_740,
    ],
  },
  2022: {
    rates: BANDED_RATES,
    bands: [
      47_014, 54_283, 57_539, 60_992, 64_652, 68_530, 72_642, 77_002, 81_621, 86_519, 91_710,
      97_213, 103_046, 109_228, 115_782, 122_729, 130_093, 137_898,
    ],
  },
  2023: {
    rates: BANDED_RATES,
    bands: [
      48_361, 55_837, 59_187, 62_739, 66_503, 70_493, 74_723, 79_207, 83_959, 88_997, 94_337,
      99_997, 105_997, 112_356, 119_098, 126_244, 133_819, 141_848,
    ],
  },
  2024: {
    rates: BANDED_RATES,
    bands: [
      51_550, 59_519, 63_090, 66_876, 70_889, 75_141, 79_650, 84_430, 89_495, 94_866, 100_558,
      106_591, 112_986, 119_765, 126_951, 134_569, 142_643, 151_201,
    ],
  },
  2025: {
    rates: BANDED_RATES,
    bands: [
      54_435, 62_851, 66_621, 70_619, 74_856, 79_347, 84_108, 89_155, 94_504, 100_175, 106_186,
      112_557, 119_310, 126_468, 134_057, 142_101, 150_627, 159_664,
    ],
  },
  // Marginal: nil to t0; 15% of the excess to t1; base + 17% of the excess
  // over t1 to t2; above t2, 10% of the whole repayment income.
  2026: { marginal: { t0: 67_000, t1: 125_000, base1: 8_700, t2: 179_285 } },
  2027: { marginal: { t0: 69_528, t1: 129_717, base1: 9_028, t2: 186_050 } },
};

/** The study-loan table for an FY (future years reuse the latest), or null before 2020–21. */
export function helpRepaymentTable(fy) {
  return HELP_REPAYMENT[fy > LATEST_THRESHOLD_FY ? LATEST_THRESHOLD_FY : fy] ?? null;
}
